import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { 
  CheckCircle, 
  Download, 
  Mail, 
  Home, 
  Star, 
  Zap,
  Users,
  FileText,
  Shield,
  ArrowRight
} from 'lucide-react'

const PaymentSuccessPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState(10)
  
  const { planName, price, paymentMethod } = location.state || {
    planName: 'Plano Selecionado',
    price: '0.00',
    paymentMethod: 'pagamento'
  }

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      navigate('/')
    }
  }, [countdown, navigate])

  const getPaymentMethodIcon = () => {
    switch (paymentMethod) {
      case 'cartao':
        return '💳'
      case 'pix':
        return '📱'
      default:
        return '✅'
    }
  }

  const getPaymentMethodText = () => {
    switch (paymentMethod) {
      case 'cartao':
        return 'Cartão de Crédito'
      case 'pix':
        return 'PIX'
      default:
        return 'Pagamento'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-primary-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', damping: 15 }}
            className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-12 h-12 text-green-600" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            Pagamento Confirmado! 🎉
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Seu pagamento foi processado com sucesso via {getPaymentMethodText()}. 
            Bem-vindo ao TANDER!
          </motion.p>
        </motion.div>

        {/* Plan Details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-12"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">{getPaymentMethodIcon()}</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {planName} Ativado
            </h2>
            <p className="text-gray-600">
              Seu plano foi ativado com sucesso
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-4 bg-primary-50 rounded-xl">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-semibold text-primary-900">Plano Ativo</h3>
              <p className="text-sm text-primary-700">Acesso imediato</p>
            </div>
            
            <div className="text-center p-4 bg-accent-50 rounded-xl">
              <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Star className="w-6 h-6 text-accent-600" />
              </div>
              <h3 className="font-semibold text-accent-900">Valor</h3>
              <p className="text-sm text-accent-700">R$ {price}/mês</p>
            </div>
            
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-green-900">Seguro</h3>
              <p className="text-sm text-green-700">100% protegido</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500 mb-4">
              Você será redirecionado para a página inicial em {countdown} segundos
            </p>
            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-gradient-to-r from-primary-500 to-accent-500 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2 mx-auto"
            >
              <Home className="w-5 h-5" />
              <span>Ir para Página Inicial</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Próximos Passos
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary-600">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email de Confirmação</h3>
                  <p className="text-sm text-gray-600">
                    Enviamos um email com todos os detalhes da sua assinatura
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary-600">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Acesso à Plataforma</h3>
                  <p className="text-sm text-gray-600">
                    Faça login para começar a usar todas as funcionalidades
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary-600">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Primeiro Upload</h3>
                  <p className="text-sm text-gray-600">
                    Comece enviando seus primeiros documentos para análise
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary-600">4</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Suporte Disponível</h3>
                  <p className="text-sm text-gray-600">
                    Nossa equipe está pronta para ajudar com qualquer dúvida
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features Preview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl p-8 text-white"
        >
          <h2 className="text-2xl font-bold mb-6 text-center">
            O que você pode fazer agora
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">Análise de Documentos</h3>
              <p className="text-sm text-primary-100">
                Upload e análise inteligente com IA
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">Colaboração em Equipe</h3>
              <p className="text-sm text-primary-100">
                Compartilhe e trabalhe em conjunto
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">Processamento Rápido</h3>
              <p className="text-sm text-primary-100">
                Resultados em segundos, não em horas
              </p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white text-primary-600 font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2 mx-auto"
            >
              <span>Começar a Usar</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>

        {/* Additional Actions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="grid md:grid-cols-2 gap-6"
        >
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Email de Confirmação</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Verifique sua caixa de entrada para o email de confirmação com todos os detalhes
            </p>
            <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
              Verificar Email →
            </button>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Download className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Comprovante</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Baixe o comprovante de pagamento para seus registros
            </p>
            <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
              Baixar Comprovante →
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default PaymentSuccessPage

